import { removeSlashes } from "../utils/dom";
import { useCallback, useEffect, useState } from "react";
import Notifications, { ToastNotifications } from "./notifications";
import { useUser, useTheme, useUI, useProduct, useAppSession, useRouterContext } from "../contexts";
import { Embeds, DASH_EXCLUDED_EMBEDS } from "./embeds";
import BotChallengeHost from "./BotChallengeHost";
import TopHeader from "./top-header";
import Spinner from "./spinner";
import LoginModal from "../components/login-modal";
import SearchBar from "./search-bar";
import NavCategoryBar from "./nav-category-bar";
import MobileInfoPopup from "./mobile-info-popup";
import AccountRail from "./account/account-rail";
import DashRail from "./dash/dash-rail";
import { DashRefreshingPill, DashRouteLoader } from "./dash/dash-loading";
import { useDashPendingRoute } from "../hooks/useDashPendingRoute";
import NavCategoryDrawer from "./nav-category-drawer";
import FilterDrawer from "./filter-drawer";
import AddressDrawer from "./address-drawer";
import ProductModal from "./product-modal";
import { isAdminEmail } from "../utils/admin";

export default function Layout({
  children,
  post = false,
  catDrawer = true,
  includeLogin = true,
  includeNotifications = true,
  header = true,
  account = false,
  search = null,
  nav = null,
  headerTitle = null,
  showAd = null,
  needsLogin = false,
  needsAdmin = false,
  titleClick = null,
  transparentBG = false,
}) {
  const [_productScroll, setProductScroll] = useState(0);
  const [productScrollPopped, setProductScrollPopped] = useState(false);

  // Use individual context hooks
  const { profile, loggedIn, isLoading } = useUser();
  const { theme } = useTheme();
  const {
    cameraHelpOpen, loginOpen, mobileTopNav, chatOpen, reportMenuOpen,
    chatMenuOpen, chatUserMenuOpen, messageMenuOpen, shareMenuOpen, countryCodeOpen,
    categoryDrawerOpen, filterDrawerOpen, notificationDrawerOpen, productOpen, searchFocused,
    shippingDrawerOpen, popupOpen, popupContents, clickClose,
    uploadSpinner,
    setMobileTopNav, setLoginOpen, setPopupOpen, setProductOpen,
    setShippingDrawerOpen, setNotificationDrawerOpen
  } = useUI();

  const { productDetail, loaded, setLoaded } = useProduct();
  const { clientSide } = useAppSession();

  // Get current path from router context (SSG-safe)
  const { pathname } = useRouterContext();
  const rap = pathname;
  const isAccountArea = (rap || "").startsWith("/account");
  const isDashArea = (rap || "").startsWith("/dash");
  // The dash page a rail click is loading, so the click is answered at once.
  const dashPending = useDashPendingRoute();
  const { replace, asPath, isReady } = useRouterContext();

  useEffect(() => {
    if (!needsLogin) return;

    // Don't redirect while still checking for session
    if (isLoading) return;

    // Nor before the router is ready. /account/my-listings/[id] has no data
    // fetching, so Next pre-renders it and its asPath IS the pattern until the
    // router hydrates. A session that resolved first sent the seller to
    // login?returnTo=/account/my-listings/[id], and the push back after login
    // threw "missing query values (id)".
    if (!isReady) return;

    // Use asPath (actual URL) instead of pathname (route pattern with [id] etc.)
    const returnPath = asPath.split('#')[0];

    if (profile?.email) {
      if (isAdminEmail(profile.email)) {
        return;
      } else {
        if (needsAdmin) {
          const q =
            returnPath === "/login"
              ? ""
              : `?returnTo=/${removeSlashes(returnPath)}`;
          replace(`/login${q}`);
          return;
        }
      }
    }

    // Check if user is logged in via NextAuth session
    // Only redirect after loading is complete
    if (!loggedIn) {
      const q =
        returnPath === "/login"
          ? ""
          : `?returnTo=/${removeSlashes(returnPath)}`;
      replace(`/login${q}`);
    }
  }, [needsLogin, needsAdmin, loggedIn, isLoading, profile?.email, asPath, isReady, replace]);

  useEffect(() => {
    // const t = $(".theme");
    // t.setAttribute("class", t.getAttribute("class") + " loaded");
    setLoaded(true);
  }, [setLoaded]);


  let cls = "theme ";
  cls += loaded ? "loaded " : " ";
  cls += (theme || "dark") + " ";
  cls += cameraHelpOpen ? "STATE--help-open " : " ";
  cls += loggedIn ? "STATE--logged-in " : "STATE--logged-out ";
  cls += loginOpen ? "STATE--login-open " : "";
  cls += needsAdmin ? " admin-dash " : "";
  cls += `STATE--mobile-top-${mobileTopNav || "left"} `;
  // cls += `STATE--mobile-bottom-${mobileBottomNav} `;
  cls += chatOpen ? "STATE--chat-open " : "";
  cls += reportMenuOpen ? "STATE--report-menu-open " : "";
  cls += chatMenuOpen ? "STATE--chat-menu-open " : "";
  cls += chatUserMenuOpen ? "STATE--chat-user-menu-open " : "";
  cls += messageMenuOpen ? "STATE--message-menu-open " : "";
  cls += shareMenuOpen ? "STATE--share-menu-open " : "";
  cls += countryCodeOpen ? "STATE--country-code-open " : "";
  cls += categoryDrawerOpen ? "STATE--nav-cat-open " : "";
  cls += filterDrawerOpen ? "STATE--filter-open " : "";
  cls += notificationDrawerOpen ? "STATE--notifications-open " : "";
  cls += productOpen ? "STATE--product-open " : "";
  cls += searchFocused ? "STATE--search-focused " : "";
  cls += shippingDrawerOpen ? "STATE--shipping-open " : "";
  cls += isAccountArea ? "STATE--account-area " : "";
  // `dash-scope` re-bases Tailwind's rem scale onto the app's 10px root and
  // swaps the stock blue-grey palette for the app's green (see tailwind.css).
  // It rides on the layout root so every dash page gets it without opting in.
  cls += isDashArea ? "STATE--dash-area dash-scope " : "";

  const handleScroll = useCallback((event) => {
    const ps = event.target.scrollTop;
    if (ps > 100 && !productScrollPopped) {
      setProductScroll(ps);
      setProductScrollPopped(true);
    }
  }, [productScrollPopped]);
  const psScroll = productScrollPopped ? "scrolling" : "";

  return (
    <div className={cls} data-testid="theme" suppressHydrationWarning>
      <Notifications />
      <ToastNotifications />
      <Embeds exclude={isDashArea ? DASH_EXCLUDED_EMBEDS : []} />
      <BotChallengeHost />
      {catDrawer ? <NavCategoryDrawer /> : ""}
      {/* One drawer for the page, wherever its Filter button is (the header's or the product overlay's search bar). */}
      {(search || productDetail) && <FilterDrawer />}
      {header && (
        <TopHeader
          post={post}
          account={account}
          showAd={showAd}
          transparentBG={transparentBG}
          headerTitle={headerTitle}
          titleClick={titleClick}
          setMobileTopNav={setMobileTopNav}
          setLoginOpen={setLoginOpen}
          setProductOpen={setProductOpen}
          setNotificationDrawerOpen={setNotificationDrawerOpen}
          includeNotifications={includeNotifications}
        >
          <>
            {(search || productDetail) && <SearchBar />}
            {(nav || productDetail) && <NavCategoryBar />}
          </>
        </TopHeader>
      )}
      <MobileInfoPopup open={popupOpen} setOpen={setPopupOpen} clickClose={clickClose || (() => setPopupOpen(false))}>
        {popupContents}
      </MobileInfoPopup>
      {/* Rendered OUTSIDE <main>: the rail + its mobile hamburger are
          position:fixed, and a transformed ancestor would make position:fixed
          resolve against that ancestor instead of the viewport. */}
      {isAccountArea && <AccountRail pathname={rap} />}
      {isDashArea && <DashRail pathname={rap} pendingPath={dashPending} />}
      {isDashArea && dashPending && <DashRouteLoader to={dashPending} />}
      {isDashArea && <DashRefreshingPill />}
      {/* Keyed by path so each page mounts fresh, as it did under the old
          page-transition wrapper. */}
      <main key={rap}>{children}</main>
      {productDetail && !pathname.startsWith('/product/') ? (
        <article
          className={`page product-page same-color product-overlay product-open siteimpact
          ${psScroll}`}
          onScroll={handleScroll}
        >
          {header && (
            <TopHeader
              post={post}
              account={account}
              showAd={showAd}
              headerTitle={headerTitle}
              titleClick={titleClick}
              transparentBG={transparentBG}
              setMobileTopNav={setMobileTopNav}
              setLoginOpen={setLoginOpen}
              setProductOpen={setProductOpen}
              setNotificationDrawerOpen={setNotificationDrawerOpen}
              includeNotifications={includeNotifications}
            >
              <>
                {search && <SearchBar />}
                {nav && <NavCategoryBar />}
                {headerTitle}
              </>
            </TopHeader>
          )}
          <ProductModal />
        </article>
      ) : null}
      {includeLogin && clientSide && (
        <LoginModal setLoginOpen={setLoginOpen} close={true} />
      )}
      {rap.includes("addresses") ? (
        ""
      ) : (
        <AddressDrawer
          shippingDrawerOpen={shippingDrawerOpen}
          setShippingDrawerOpen={setShippingDrawerOpen}
        />
      )}
      {uploadSpinner ? <Spinner /> : ""}
    </div>
  );
}
